/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import queryString from "query-string";

import { APIRoute } from "src/classes/api/API";
export class HTTPMethods {
  static baseURL = process.env.API_URL;

  private static get token(): string {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("token") || "";
  }

  private static get authHeader(): { authorization: string } {
    return { authorization: `Bearer ${this.token}` };
  }

  private static get corsHeader(): { mode?: "no-cors" } {
    return process.env.ENVIRONMENT === "local" ? { mode: "no-cors" } : {};
  }

  /* -------------------------------------------------- */
  /*                    HTTP Methods                    */
  /* -------------------------------------------------- */
  public async post<Body extends Object, Res>(route: APIRoute, body: Body): Promise<Res> {
    const config = {
      method: "POST",
      headers: { "Content-Type": "application/json", ...HTTPMethods.authHeader, ...HTTPMethods.corsHeader },
      body: JSON.stringify(body),
    };
    const url = new URL(`${HTTPMethods.baseURL}/${route}`);
    const res = await fetch(url.toString(), config);
    const data = await this.handleResponse<Res>(res);
    return data;
  }

  public async get<Params extends Object, Res>(route: APIRoute, params?: Params): Promise<Res> {
    let url = new URL(`${HTTPMethods.baseURL}/${route}`);
    if (params && Object.keys(params).length > 0) {
      url.search = queryString.stringify(params);
    }
    const config = {
      method: "GET",
      headers: { "Content-Type": "application/json", ...HTTPMethods.authHeader, ...HTTPMethods.corsHeader },
    };

    const res = await fetch(url.toString(), config);
    const data = await this.handleResponse<Res>(res);
    return data as Res;
  }

  public async delete<Params extends Object>(route: APIRoute, params: Params): Promise<void> {
    const url = new URL(`${HTTPMethods.baseURL}/${route}`);
    Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, value.toString()));
    const config = {
      method: "DELETE",
      headers: { ...HTTPMethods.authHeader, ...HTTPMethods.corsHeader },
    };
    await fetch(url.toString(), config);
  }

  public async patch<Body extends Object, Res>(route: APIRoute, body: Body): Promise<Res | undefined> {
    const config = {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...HTTPMethods.authHeader, ...HTTPMethods.corsHeader },
      body: JSON.stringify(body),
    };
    const url = new URL(`${HTTPMethods.baseURL}/${route}`);
    const res = await fetch(url.toString(), config);
    const data = await this.handleResponse<Res>(res);
    return data;
  }

  public async put<Body extends Object, Res>(route: APIRoute, body: Body): Promise<Res | undefined> {
    const config = {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...HTTPMethods.authHeader, ...HTTPMethods.corsHeader },
      body: JSON.stringify(body),
    };
    const url = new URL(`${HTTPMethods.baseURL}/${route}`);
    const res = await fetch(url.toString(), config);
    const data = await this.handleResponse<Res>(res);
    return data;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const data = await response.json();
    if (!response.ok) {
      const message = data.message || "An error occurred during the request.";
      throw new HTTPError(response.status, data, message);
    }
    return data;
  }
}

/* -------------------------------------------------- */
/*                    Error Class                     */
/* -------------------------------------------------- */
class HTTPError extends Error {
  public status: number;

  public data: any;

  constructor(status: number, data: any, message: string) {
    super(message);
    this.status = status;
    this.data = data;
  }
}
